import React from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { IconChevronDown, IconGripVertical, IconPackage, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getContrastColor, stringToColor, COLOR_DANGER } from '../../../../shared/colorUtils';
import CaseHeaderInfo from './CaseHeaderInfo';
import CaseUnitPicker from './CaseUnitPicker';
import { bundleDropId, contentDragId } from './packingActions';

const packedCount = (bundle) => (bundle.contents || []).reduce((sum, c) => sum + c.quantity, 0);

const ContentRow = ({ content, index, onUnpack, disabled }) => {
  const { t } = useTranslation();
  return (
    <Draggable draggableId={contentDragId(content.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`d-flex align-items-center gap-2 px-2 py-1 rounded mb-1 border${snapshot.isDragging ? ' shadow' : ''}`}
          style={{
            background: snapshot.isDragging ? 'var(--tblr-azure-lt)' : 'var(--tblr-bg-surface)',
            ...provided.draggableProps.style,
          }}
        >
          <span {...provided.dragHandleProps} className="text-muted" style={{ cursor: 'grab', lineHeight: 1 }}>
            <IconGripVertical size={14} />
          </span>
          <span className="flex-grow-1 small">
            <span className="fw-medium">{content.product_name}</span>
            {content.serial_number && <span className="text-muted ms-1">· {content.serial_number}</span>}
          </span>
          {content.kind === 'unit'
            ? <code className="small text-muted">{content.qr_code}</code>
            : <Badge bg="info" className="fw-normal">×{content.quantity}</Badge>}
          <button
            type="button"
            className="btn-close"
            style={{ fontSize: '0.55rem' }}
            disabled={disabled}
            onClick={() => onUnpack(content)}
            title={t('projects.cases.unassignItem')}
            aria-label={t('projects.cases.unassignItem')}
          />
        </div>
      )}
    </Draggable>
  );
};

/** One project case: header with weight/dimensions/fit, box choice and a droppable list of its contents. */
const CaseCard = ({ bundle, collapsed, onToggle, onRemove, onUnpack, onCaseUnitChange, busy }) => {
  const { t } = useTranslation();
  const contents = bundle.contents || [];
  const capacity = bundle.bundle_items_per_case || 0;
  const count = packedCount(bundle);
  const doesNotFit = ['too_big', 'over_volume'].includes(bundle.fit?.status);
  const headerBg = doesNotFit ? COLOR_DANGER : stringToColor(bundle.bundle_name || '');
  const headerColor = getContrastColor(headerBg);
  const hasTemplate = (bundle.template || []).length > 0;

  return (
    <Card className={collapsed ? '' : 'h-100'} data-testid={`case-${bundle.id}`}>
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2 rounded-top"
        style={{ background: headerBg, color: headerColor, cursor: 'pointer', userSelect: 'none' }}
        onClick={onToggle}
      >
        <div className="d-flex align-items-center gap-2 overflow-hidden">
          <IconPackage size={16} />
          <strong className="text-truncate">{bundle.bundle_name}</strong>
        </div>
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          <Badge style={{ background: 'rgba(0,0,0,0.2)', color: headerColor }}>
            {!hasTemplate && capacity > 0 ? `${count}/${capacity}` : count}
          </Badge>
          <Button
            size="sm"
            variant="link"
            className="py-0 px-1"
            style={{ color: headerColor, opacity: 0.8 }}
            onClick={(e) => { e.stopPropagation(); onRemove(bundle); }}
            title={t('projects.cases.removeCase')}
          >
            <IconTrash size={14} />
          </Button>
          <IconChevronDown
            size={16}
            style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.2s' }}
          />
        </div>
      </div>
      <div className="px-3 py-2 border-bottom">
        <CaseHeaderInfo bundle={bundle} />
        <div className="mt-2">
          <CaseUnitPicker bundle={bundle} disabled={busy} onChange={(caseUnit) => onCaseUnitChange(bundle, caseUnit)} />
        </div>
      </div>
      <Card.Body className="p-2" style={collapsed ? {} : { maxHeight: 260, overflowY: 'auto' }}>
        <Droppable droppableId={bundleDropId(bundle.id)}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                minHeight: collapsed ? 24 : 56,
                background: snapshot.isDraggingOver ? 'var(--tblr-primary-lt)' : 'transparent',
                borderRadius: 4,
                transition: 'background 0.15s',
              }}
            >
              {!collapsed && contents.map((content, index) => (
                <ContentRow key={content.id} content={content} index={index} onUnpack={onUnpack} disabled={busy} />
              ))}
              {provided.placeholder}
              {(collapsed || contents.length === 0) && (
                <div className="text-muted small fst-italic py-2 text-center">{t('projects.cases.dropHere')}</div>
              )}
            </div>
          )}
        </Droppable>
      </Card.Body>
    </Card>
  );
};

export default CaseCard;
